import { Box, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
	fetchApiTablePropertiesGenerateProfile,
	useApiHasPreviewAccess,
} from '@repo/api-codegen';
import { find } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import type { ApiCatalogSort, IColumn, ISecodaEntity } from '../../../../api';
import { queryClient, useAuthUser, useWorkspace } from '../../../../api';
import {
	fetchCatalogList,
	useCatalogInfiniteList,
} from '../../../../api/hooks/resourceCatalog';
import { useColumnDefs } from '../../../../components/CatalogView/hooks/useColumnDefs';
import type { CatalogServerType } from '../../../../components/CatalogView/types';
import { TableV2 } from '../../../../components/TableV2';

import { trackEvent } from '@repo/common/analytics';
import { FilterOptionType } from '@repo/common/components/Filter/types';
import { EntityType } from '@repo/common/enums/entityType';
import type {
	FetchModelInfiniteListHook,
	FetchModelList,
} from '../../../../api/factories/types';
import { resourceCatalogQueryKeyFactory } from '../../../../api/hooks/resourceCatalog/constants';
import { FILTER_OPTIONS_CONFIG } from '../../../../components/Filter/constants';
import { useCatalogFilter } from '../../../../components/Filter/useCatalogFilter';
import { PROFILER_COLUMN_NAME } from '../../../../constants';
import { BackgroundJob } from '../../../../lib/models';
import { useCheckEntityUpdatePermission } from '../../../../utils/authorization/roles';
import { insertSemanticViewTypeFilter } from '../utils';
import ColumnProfiler from './ColumnProfiler';
import { useStyles } from './ColumnsTab.styles';
import { useActions, useColumns } from './hooks';

const WITH_CUSTOM_PROPERTIES = (entityType: EntityType) => [entityType];

interface IColumnsTabProps {
	table: ISecodaEntity;
	entityType?: EntityType;
}

const COLUMN_VISIBILITY = (entityType: EntityType) => {
	const catalogServerType =
		entityType === EntityType.generic
			? ('secoda_entity' as const)
			: (entityType as EntityType & CatalogServerType);
	return {
		catalogType: entityType,
		catalogServerType,
	} as const;
};

const DEFAULT_SEARCH_PARAMS = {
	calculate_children_count: true,
} as const;

function ColumnsTab({
	table,
	entityType = EntityType.column,
}: IColumnsTabProps) {
	const canUpdateTable = useCheckEntityUpdatePermission({
		entity: table,
	});
	const columns = useColumns({
		forceReadOnly: !canUpdateTable,
		table,
		entityType,
	});
	const actions = useActions();

	const { classes } = useStyles();
	const { isEditorOrAdminUser, user } = useAuthUser();
	const { data: previewPermissionGranted } = useApiHasPreviewAccess(
		{
			pathParams: {
				integrationId: table?.integration ?? '',
			},
		},
		{
			enabled: !!table?.integration,
		}
	);
	const { workspace } = useWorkspace();

	const [backgroundJob, setBackgroundJob] = useState<BackgroundJob | null>(
		null
	);

	const { catalog, onAddColumn } = useColumnDefs({
		defaultColumns: columns,
		catalogType: entityType,
		catalogServerType:
			entityType === EntityType.generic
				? ('secoda_entity' as const)
				: (entityType as EntityType & CatalogServerType),
		isEditorOrAdminUser,
	});

	const handleRunProfilerClick = useCallback(async () => {
		const job = await fetchApiTablePropertiesGenerateProfile({
			pathParams: {
				entityId: table.id,
			},
		});
		setBackgroundJob(new BackgroundJob(job));
		trackEvent('table/profiler/click', {}, user, workspace);
	}, [table.id, user, workspace]);

	const handleBackgroundJobCompleted = useCallback(async () => {
		setBackgroundJob(null);

		const hasProfilerColumn = find(catalog?.properties, {
			field: PROFILER_COLUMN_NAME,
		});
		if (hasProfilerColumn === undefined) {
			await onAddColumn(PROFILER_COLUMN_NAME);
		}

		trackEvent('table/profiler/completed', {}, user, workspace);
		showNotification({
			title: 'Profiling completed',
			message: 'Table profiling completed successfully',
		});

		queryClient.invalidateQueries({
			queryKey: resourceCatalogQueryKeyFactory.allLists(),
		});
	}, [catalog?.properties, onAddColumn, user, workspace]);

	const defaultSort: ApiCatalogSort = {
		field: 'sort_order',
		order: 'asc',
	};

	const baseFilterOptions = useCatalogFilter();
	const isSemanticView =
		table?.native_type === 'semantic_view' ||
		table?.native_type === 'dbt_semantic_model';
	const filterOptions = useMemo(() => {
		const semanticViewTypeFilter =
			entityType === EntityType.column
				? FILTER_OPTIONS_CONFIG[FilterOptionType.SEMANTIC_VIEW_COLUMN_TYPE]
				: FILTER_OPTIONS_CONFIG[FilterOptionType.SEMANTIC_VIEW_GENERIC_TYPE];
		return insertSemanticViewTypeFilter(
			baseFilterOptions,
			semanticViewTypeFilter,
			isSemanticView
		);
	}, [baseFilterOptions, isSemanticView, entityType]);

	return (
		<Stack className={classes.gridWrapper}>
			<Box className={classes.tableWrapper}>
				<TableV2<IColumn>
					withCustomProperties={WITH_CUSTOM_PROPERTIES(entityType)}
					key={columns.length}
					withSearch
					withFilterOptions={filterOptions}
					withInteractiveHeader
					withCsvExport
					withServerCsvExport
					withAddProperty={table.id}
					withCheckbox={canUpdateTable}
					withActions={actions}
					withAdditionalButtons={
						isEditorOrAdminUser &&
						previewPermissionGranted && (
							<ColumnProfiler
								backgroundJob={backgroundJob}
								onRunProfilerClick={handleRunProfilerClick}
								onBackgroundJobCompleted={handleBackgroundJobCompleted}
							/>
						)
					}
					columns={columns}
					withInfiniteScroll
					usePaginationList={
						useCatalogInfiniteList as FetchModelInfiniteListHook<IColumn>
					}
					columnVisibility={COLUMN_VISIBILITY(entityType)}
					defaultRequiredCatalogFilters={{
						operands: [
							{
								operands: [],
								field: 'parent_id',
								operator: 'exact',
								value: table.id,
							},
						],
					}}
					defaultRequiredSearchParams={{
						...DEFAULT_SEARCH_PARAMS,
						entity_type: entityType,
					}}
					defaultRequiredSearchParamsNesting={{
						...DEFAULT_SEARCH_PARAMS,
						entity_type: entityType,
					}}
					defaultSort={defaultSort}
					nestingFilter="parent_id"
					fetchPaginationList={
						fetchCatalogList as unknown as FetchModelList<IColumn>
					}
					usePaginationListOptions={{
						suspense: true,
					}}
				/>
			</Box>
		</Stack>
	);
}

const ColumnsTabWithObserver = observer(ColumnsTab);
export default ColumnsTabWithObserver;
